import * as THREE from "three";
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from "three-mesh-bvh";
import type { MeasurementType } from "../types.ts";
import { AnnotationManager, computeMeasurementResult } from "./Annotation.ts";
import { snapToVertex } from "./Snap.ts";

// Patch THREE to use BVH raycasting
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

export { computeBoundsTree };

/** Number of points needed to complete each measurement type. */
const REQUIRED_POINTS: Record<MeasurementType, number> = {
  distance: 2,
  height: 2,
  point: 1,
  area: -1, // variable; right-click or Enter to finish
};

export interface MeasureResult {
  type: MeasurementType;
  points: THREE.Vector3[];
  value: number;
  unit: string;
}

export class MeasureTool {
  private active = false;
  private mode: MeasurementType | null = null;
  private points: THREE.Vector3[] = [];
  private raycaster = new THREE.Raycaster();
  private meshes: THREE.Mesh[] = [];
  private cursor = new THREE.Vector3();

  onComplete: ((r: MeasureResult) => void) | null = null;
  onCancel: (() => void) | null = null;
  onCursorChange: ((active: boolean) => void) | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: THREE.Camera,
    private annotations: AnnotationManager,
  ) {
    this.raycaster.firstHitOnly = true;
    this._bindEvents();
  }

  /** Start measuring in the given mode against the provided meshes. */
  activate(mode: MeasurementType, meshes: THREE.Mesh[]): void {
    this.mode = mode;
    this.meshes = meshes;
    this.points = [];
    this.active = true;
    this.canvas.style.cursor = "crosshair";
    this.onCursorChange?.(true);
  }

  deactivate(): void {
    this.active = false;
    this.mode = null;
    this.points = [];
    this.canvas.style.cursor = "";
    this.annotations.clearPreview();
    this.onCursorChange?.(false);
  }

  /** Build BVH on all meshes in a scene for fast raycasting. */
  static buildBVH(root: THREE.Object3D): void {
    root.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.geometry && !obj.geometry.boundsTree) {
        obj.geometry.computeBoundsTree();
      }
    });
  }

  dispose(): void {
    this._unbindEvents();
    this.annotations.clearPreview();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _castRay(event: MouseEvent): THREE.Vector3 | null {
    const rect = this.canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );

    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.meshes, true);
    if (!hits.length) return null;

    return snapToVertex(hits[0], this.camera, this.canvas);
  }

  private _onMouseMove = (e: MouseEvent): void => {
    if (!this.active || !this.mode) return;
    const pt = this._castRay(e);
    if (pt) {
      this.cursor.copy(pt);
      this.annotations.setPreview([...this.points, pt], this.mode);
    } else {
      this.annotations.setPreview(this.points, this.mode);
    }
  };

  private _onClick = (e: MouseEvent): void => {
    if (!this.active || !this.mode || e.button !== 0) return;
    const pt = this._castRay(e);
    if (!pt) return;

    this.points.push(pt);

    const required = REQUIRED_POINTS[this.mode];
    if (required > 0 && this.points.length >= required) {
      this._complete();
    }
  };

  private _onDblClick = (_e: MouseEvent): void => {
    if (!this.active || this.mode !== "area") return;
    // The two preceding click events each added a point — remove the duplicate
    // added by the second click so the double-click location is counted only once.
    if (this.points.length > 1) this.points.pop();
    if (this.points.length >= 3) this._complete();
  };

  private _onContextMenu = (e: MouseEvent): void => {
    if (!this.active || this.mode !== "area") return;
    e.preventDefault();
    if (this.points.length >= 3) this._complete();
  };

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) return;
    if (e.key === "Escape") {
      this.annotations.clearPreview();
      this.deactivate();
      this.onCancel?.();
    }
    if (e.key === "Enter" && this.mode === "area" && this.points.length >= 3) {
      this._complete();
    }
  };

  private _complete(): void {
    if (!this.mode) return;
    const { value, unit } = computeMeasurementResult(this.mode, this.points);
    const result: MeasureResult = { type: this.mode, points: [...this.points], value, unit };
    this.annotations.clearPreview();
    this.deactivate();
    this.onComplete?.(result);
  }

  private _bindEvents(): void {
    this.canvas.addEventListener("mousemove", this._onMouseMove);
    this.canvas.addEventListener("click", this._onClick);
    this.canvas.addEventListener("dblclick", this._onDblClick);
    this.canvas.addEventListener("contextmenu", this._onContextMenu);
    window.addEventListener("keydown", this._onKeyDown);
  }

  private _unbindEvents(): void {
    this.canvas.removeEventListener("mousemove", this._onMouseMove);
    this.canvas.removeEventListener("click", this._onClick);
    this.canvas.removeEventListener("dblclick", this._onDblClick);
    this.canvas.removeEventListener("contextmenu", this._onContextMenu);
    window.removeEventListener("keydown", this._onKeyDown);
  }
}
